import React, { useState, useContext } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { login } from "../../../Services/authapis";
import { toast } from "react-toastify";
import LOCAL_KEYS, { parseLocalStorageJSON, setToLocalStorage } from "../../../helpers/localstorage";
import { useNavigate, Link } from "react-router-dom";
import { FaRegEyeSlash, FaEye } from "react-icons/fa";
import datacloud from "../../../Assets/unisoft.jpg";
import { UserContext } from "../../../Context/User";
import TextInput from "../../../components/TextInput";
 
const LoginForm = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const { setCurrentUserDetails, updatePasswordExpiredState } = useContext(UserContext);
 
  // Validation schema using Yup
  const LoginSchema = Yup.object().shape({
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });
 
  return (
    <div className="flex items-center justify-center w-[80vw] sm:w-[60vw] md:w-[24vw] p-3 md:px-5 bg-white rounded-2xl h-[50vh] sm:h-[55vh]">
      <Formik
        initialValues={{
          username: "",
          password: "",
        }}
        validationSchema={LoginSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const response = await login(values.username, values.password);
           
            if (!response || !response.access_token) {
              toast.error("Invalid response from server");
              return;
            }
           
            // Store auth details in localStorage
            localStorage.setItem("access_token", response.access_token);
           
            // Make sure userDetails exists before storing
            if (response.userDetails) {
              setToLocalStorage(LOCAL_KEYS.USER_DETAILS, response.userDetails);
              setToLocalStorage(LOCAL_KEYS.ACCESS_TOKEN, response.access_token);
              setToLocalStorage(LOCAL_KEYS.PROFILE_DETAILS, response.userDetails);
              setCurrentUserDetails(response.userDetails);
              
              // Check for password expiry
              if (response.userDetails.passwordExpired === true) {
                // Set password expired flag using context method
                updatePasswordExpiredState(true);
                toast.error("Your password has expired. Please change your password now.");
                navigate("/updatePassword");
                return;
              }
              
              // Clear any existing password expired flag
              updatePasswordExpiredState(false);
              
              toast.success("Login successful!");
             
              // Navigate based on user type - we'll just go to dashboard and let it handle the redirection
              navigate("/");
            } else {
              toast.error("User details not received");
            }
          } catch (error) {
            toast.error(error.response?.data?.message || "Username or password is invalid! Please try again");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, isSubmitting, values, handleChange }) => (
          <Form className="shadow-none w-full h-full mt-5 sm:mt-10">
            <div className="text-center mb-6 sm:mb-8">
              <span className="flex items-center justify-center p-4">
                <img src={datacloud} alt="Logo" className="h-16 sm:h-20 w-auto object-contain" />
              </span>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#201F1E] mb-2">
                Welcome Back
              </h1>
              <p className="text-md sm:text-lg flex items-center justify-center mb-6 sm:mb-10">
                Please Sign in to continue
              </p>
            </div>
            <div className="mb-4">
              <TextInput
                type="text"
                name="username"
                placeholder="Username"
                value={values.username}
                handleChange={handleChange}
                legend={true}
                //required={true}
                classes="w-full"
              />
              {errors.username && touched.username ? (
                <p className="text-red-400">{errors.username}</p>
              ) : null}
            </div>
            <div className="relative my-6 sm:my-8">
              <TextInput
                type={isPasswordVisible ? "text" : "password"}
                name="password"
                placeholder="Password"
                value={values.password}
                handleChange={handleChange}
                legend={true}
                //required={true}
                classes="w-full pr-10"
              />
              <span
                className="absolute right-2 top-3 flex items-center px-2 cursor-pointer"
                onClick={() => setIsPasswordVisible((v) => !v)}
              >
                {!isPasswordVisible ? <FaRegEyeSlash /> : <FaEye />}
              </span>
              {errors.password && touched.password ? (
                <p className="text-red-400">{errors.password}</p>
              ) : null}
            </div>
            <div className="mb-6 flex items-center justify-end">
              <Link className="flex text-primary mr-2 float-right" to={"/forgotpassword"}>
                Forgot Password?
              </Link>
            </div>
            <button
              type="submit"
              className="w-full text-white font-medium text-lg bg-[#201F1E] hover:bg-[#201F1E] p-2 rounded"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Logging in..." : "Submit"}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};
 
export default LoginForm;